import React, { useEffect, useState } from "react";
import { AlarmClock, Loader2, Save, Send } from "lucide-react";
import api from "../../api";
import { useToast } from "../../hooks/use-toast";

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "am" : "pm"}`;

// One email a morning listing everything still waiting. Every part of it is set here so the
// timing and the contents never need a deploy.
export const AdminReviewDigest = () => {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/admin/settings/review-digest").then((r) => setD(r.data)).catch(() => setD(null));
  }, []);

  const set = (patch) => setD((p) => ({ ...p, ...patch }));

  const save = async () => {
    setBusy("save");
    try {
      const { data } = await api.post("/admin/settings/review-digest", {
        enabled: !!d.enabled, localHour: Number(d.localHour), includeDeals: !!d.includeDeals,
        includeReports: !!d.includeReports, includeVerifications: !!d.includeVerifications,
        minAgeHours: Number(d.minAgeHours) || 0, to: d.to || "",
      });
      setD((p) => ({ ...p, ...data }));
      toast({ title: "Digest saved", description: d.enabled ? `Sending at ${hourLabel(Number(d.localHour))} Trinidad time.` : "The digest is off." });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not save that" }); }
    finally { setBusy(""); }
  };

  const sendNow = async () => {
    setBusy("test");
    try {
      const { data } = await api.post("/admin/settings/review-digest/test");
      toast({ title: data.empty ? "Nothing is waiting" : data.sent ? `Sent to ${data.to}` : "Could not send that",
        description: data.empty ? data.message : "" });
    } catch (e) { toast({ title: e?.response?.data?.detail || "Could not send that" }); }
    finally { setBusy(""); }
  };

  if (!d) return null;
  const waiting = (d.waiting?.deals || 0) + (d.waiting?.reports || 0) + (d.waiting?.verifications || 0);

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-review-digest">
      <h2 className="font-bold text-foreground flex items-center gap-2">
        <AlarmClock size={18} className="text-[#16a34a]" /> Review queue digest
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5">
        One email a morning listing what's still waiting on you, so nothing sits for days. Nothing
        waiting means no email. Right now {waiting === 0 ? "the queue is clear" : `${waiting} item${waiting === 1 ? "" : "s"} would be listed`}.
        {d.lastSentAt ? ` Last sent ${new Date(d.lastSentAt).toLocaleString()}.` : ""}
      </p>

      <label className="flex items-center gap-3 mt-4 text-sm font-semibold text-foreground cursor-pointer">
        <input type="checkbox" checked={!!d.enabled} onChange={(e) => set({ enabled: e.target.checked })}
          data-testid="digest-enabled" className="h-4 w-4 accent-[#16a34a]" />
        Send the daily digest
      </label>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="text-xs font-semibold text-foreground block mb-1">Send at (Trinidad time)</label>
          <select value={d.localHour} onChange={(e) => set({ localHour: e.target.value })} data-testid="digest-hour"
            className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground">
            {HOURS.map((h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-foreground block mb-1">Only list things older than</label>
          <select value={d.minAgeHours} onChange={(e) => set({ minAgeHours: e.target.value })} data-testid="digest-min-age"
            className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground">
            <option value={0}>Anything waiting</option>
            <option value={2}>2 hours</option>
            <option value={6}>6 hours</option>
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs font-semibold text-foreground block mb-1">Send to</label>
          <input value={d.to || ""} onChange={(e) => set({ to: e.target.value })} data-testid="digest-to"
            placeholder="support@lytepress.com"
            className="w-full border border-border rounded-xl px-3 py-2.5 text-sm bg-transparent outline-none focus:border-[#16a34a] text-foreground" />
        </div>
      </div>

      <p className="text-xs font-semibold text-foreground mt-4">Include</p>
      <div className="space-y-1.5 mt-1.5">
        {[["includeDeals", "Deals waiting for approval", d.waiting?.deals],
          ["includeReports", "Open shopper reports", d.waiting?.reports],
          ["includeVerifications", "Verification requests", d.waiting?.verifications]].map(([key, label, n]) => (
          <label key={key} className="flex items-center gap-3 text-sm text-foreground cursor-pointer">
            <input type="checkbox" checked={!!d[key]} onChange={(e) => set({ [key]: e.target.checked })}
              data-testid={`digest-${key}`} className="h-4 w-4 accent-[#16a34a]" />
            {label} <span className="text-xs text-muted-foreground">({n || 0} now)</span>
          </label>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        <button onClick={save} disabled={!!busy} data-testid="digest-save-btn"
          className="inline-flex items-center gap-2 bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-xl transition-colors">
          {busy === "save" ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save
        </button>
        <button onClick={sendNow} disabled={!!busy} data-testid="digest-test-btn"
          className="inline-flex items-center gap-2 border border-border hover:border-[#16a34a] disabled:opacity-60 text-foreground font-semibold px-5 py-2.5 rounded-xl transition-colors">
          {busy === "test" ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send me one now
        </button>
      </div>
    </div>
  );
};

export default AdminReviewDigest;
